import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toProperCase } from '../utils/textFormat';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Search, Building2, Mail, Phone, Plus, Trash2, Pencil, Loader2, CreditCard } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { SelectOrgPrompt, useNeedsOrg } from '../components/SelectOrgPrompt';
import { isValidEmail, formatPhone, rawDigits, isValidPhone } from '../utils/claimHelpers';
import UpgradeLimitPrompt from '../components/UpgradeLimitPrompt';
import NotificationRecipientsCard from '../components/NotificationRecipientsCard';

import { API_BASE as API } from '../config';

const EMPTY_COMPANY = { name: '', dot_number: '', city: '', state: '', zip: '', contact: '', email: '', phone: '', org_id: '' };

const Admin = () => {
  const { user, selectedTenants, hasRole, operatorContext, userOrgs } = useAuth();
  const needsOrg = useNeedsOrg();
  const isOperatorInContext = operatorContext?.tenantId;

  // Companies state
  const [companies, setCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [companyForm, setCompanyForm] = useState(EMPTY_COMPANY);
  const [formErrors, setFormErrors] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [limitPrompt, setLimitPrompt] = useState({ open: false, message: '' });
  const [dotLooking, setDotLooking] = useState(false);

  const handleDotLookup = async () => {
    const dot = companyForm.dot_number?.trim();
    if (!dot) return;
    setDotLooking(true);
    try {
      const res = await axios.get(`${API}/fmcsa/lookup/${dot}`, { withCredentials: true });
      const { legal_name, city, state, zip } = res.data;
      if (legal_name) {
        setCompanyForm((prev) => ({ ...prev, name: legal_name, city: city || '', state: state || '', zip: zip || '' }));
        toast.success(`Found: ${legal_name}`);
      } else {
        toast.error('No legal name returned for that DOT#');
      }
    } catch (err) {
      const msg = err.response?.status === 404 ? 'No carrier found for that DOT#' : 'FMCSA lookup failed';
      toast.error(msg);
    } finally {
      setDotLooking(false);
    }
  };

  useEffect(() => { fetchCompanies(); }, [selectedTenants]);

  useEffect(() => {
    setFilteredCompanies(
      companies.filter(c =>
        c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (c.contact && c.contact.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    );
  }, [searchTerm, companies]);

  const fetchCompanies = async () => {
    try {
      const res = await axios.get(`${API}/companies`);
      setCompanies(res.data);
      setFilteredCompanies(res.data);
    } catch { toast.error('Failed to fetch companies'); }
    finally { setLoading(false); }
  };

  const validateForm = () => {
    const errors = {};
    if (!companyForm.name.trim()) errors.name = 'Company name is required';
    if (companyForm.email && !isValidEmail(companyForm.email)) errors.email = 'Invalid email format';
    if (companyForm.phone && !isValidPhone(companyForm.phone)) errors.phone = 'Phone must be 10 digits';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const openAddModal = async () => {
    try {
      const res = await axios.get(`${API}/billing/check-limit/company`);
      if (!res.data.allowed) {
        setLimitPrompt({ open: true, message: res.data.reason });
        return;
      }
    } catch (err) { console.warn('[Admin] company limit check failed (proceeding)', err); }
    // Check billing impact for paid subscriptions
    try {
      const impactRes = await axios.post(`${API}/billing/impact`, {
        resource_type: 'company',
        action: 'add',
      }, { withCredentials: true });
      if (impactRes.data.has_impact) {
        if (!window.confirm(`${impactRes.data.message}\n\nProceed to add company?`)) return;
      }
    } catch (err) { console.warn('[Admin] billing impact check failed (proceeding)', err); }
    setEditingCompany(null);
    setCompanyForm(EMPTY_COMPANY);
    setFormErrors({});
    setShowModal(true);
  };

  const openEditModal = (company) => {
    setEditingCompany(company.name);
    setCompanyForm({
      name: company.name || '',
      dot_number: company.dot_number || '',
      city: company.city || '',
      state: company.state || '',
      zip: company.zip || '',
      contact: company.contact || '',
      email: company.email || '',
      phone: company.phone ? formatPhone(rawDigits(company.phone)) : '',
      org_id: company.org_id || '',
      _claim_count: company.claim_count || 0,
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleSaveCompany = async () => {
    if (!validateForm()) return;
    const payload = {
      ...companyForm,
      phone: rawDigits(companyForm.phone),
      org_id: companyForm.org_id || null,
    };
    try {
      if (editingCompany) {
        await axios.put(`${API}/companies/${encodeURIComponent(editingCompany)}`, payload);
        toast.success('Company updated');
      } else {
        await axios.post(`${API}/companies`, payload);
        toast.success('Company added');
      }
      setShowModal(false);
      fetchCompanies();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save company');
    }
  };

  const [billingImpact, setBillingImpact] = useState(null);

  const handleDeleteCompany = async (name) => {
    try {
      await axios.delete(`${API}/companies/${encodeURIComponent(name)}`);
      toast.success(`"${name}" deleted`);
      setDeleteTarget(null);
      setBillingImpact(null);
      fetchCompanies();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete company');
    }
  };

  const initiateDelete = async (name) => {
    // Fetch billing impact before showing confirm dialog
    try {
      const res = await axios.post(`${API}/billing/impact`, {
        resource_type: 'company',
        action: 'remove',
      }, { withCredentials: true });
      setBillingImpact(res.data.has_impact ? res.data : null);
    } catch {
      setBillingImpact(null);
    }
    setDeleteTarget(name);
  };

  const displayPhone = (phone) => {
    if (!phone) return '-';
    const d = rawDigits(phone);
    if (d.length === 10) return formatPhone(d);
    return phone;
  };

  if (loading) return <div className="min-h-screen bg-gray-200 p-4 md:p-8 text-center">Loading...</div>;
  if (needsOrg && !isOperatorInContext) return <SelectOrgPrompt />;

  return (
    <div className="min-h-screen bg-gray-200 p-4 md:p-8" data-testid="admin-page">
      <div className="max-w-7xl mx-auto">
        {/* Companies */}
        <Card className="border-none shadow-sm" data-testid="companies-management">
            <CardHeader className="px-4 md:px-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <CardTitle className="text-xl md:text-2xl font-bold text-blue-500 flex items-center gap-2">
                  <Building2 className="w-5 md:w-6 h-5 md:h-6" />
                  Company Management
                </CardTitle>
                <Button onClick={openAddModal} className="bg-blue-500 hover:bg-blue-600 text-white w-full sm:w-auto" data-testid="add-company-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add New Company
                </Button>
              </div>
            </CardHeader>
            <CardContent className="px-4 md:px-6">
              <div className="mb-4 md:mb-6 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input placeholder="Search companies by name or contact..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="company-search" />
              </div>
              <div className="border rounded-lg overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="font-semibold">Company Name</TableHead>
                      <TableHead className="font-semibold">DOT#</TableHead>
                      <TableHead className="font-semibold">City/State</TableHead>
                      <TableHead className="font-semibold">Contact Person</TableHead>
                      <TableHead className="font-semibold">Email</TableHead>
                      <TableHead className="font-semibold">Phone</TableHead>
                      <TableHead className="font-semibold text-center">Claims</TableHead>
                      <TableHead className="font-semibold">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredCompanies.length > 0 ? filteredCompanies.map((company, i) => (
                      <TableRow key={i} className="hover:bg-gray-50 transition-colors" data-testid={`company-row-${i}`}>
                        <TableCell className="font-medium">{company.name}</TableCell>
                        <TableCell className="font-mono text-sm">{company.dot_number || '-'}</TableCell>
                        <TableCell className="text-sm">{[company.city, company.state].filter(Boolean).join(', ') || '-'}{company.zip ? ` ${company.zip}` : ''}</TableCell>
                        <TableCell>{company.contact || '-'}</TableCell>
                        <TableCell>
                          {company.email ? <a href={`mailto:${company.email}`} className="text-blue-500 hover:underline flex items-center gap-1"><Mail className="w-3 h-3" />{company.email}</a> : '-'}
                        </TableCell>
                        <TableCell>
                          {company.phone ? <a href={`tel:${rawDigits(company.phone)}`} className="text-blue-500 hover:underline flex items-center gap-1"><Phone className="w-3 h-3" />{displayPhone(company.phone)}</a> : '-'}
                        </TableCell>
                        <TableCell className="text-center">
                          <span className={`text-sm font-medium ${company.claim_count > 0 ? 'text-gray-900' : 'text-gray-400'}`}>{company.claim_count || 0}</span>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button variant="outline" size="sm" className="text-blue-500 border-blue-200 hover:bg-blue-50" onClick={() => openEditModal(company)} data-testid={`edit-company-${i}`}>
                              <Pencil className="w-3 h-3 mr-1" /> Edit
                            </Button>
                            <Button
                              variant="outline" size="sm"
                              className={company.claim_count > 0 ? 'text-gray-400 border-gray-200 cursor-not-allowed opacity-50' : 'text-red-500 border-red-200 hover:bg-red-50'}
                              onClick={() => company.claim_count > 0 ? toast.error(`Cannot delete — this company has ${company.claim_count} active claim(s)`) : initiateDelete(company.name)}
                              data-testid={`delete-company-${i}`}
                            >
                              <Trash2 className="w-3 h-3 mr-1" /> Delete
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    )) : (
                      <TableRow><TableCell colSpan={9} className="text-center py-8 text-gray-500">No companies found matching your search.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
              <div className="mt-4 text-sm text-gray-600">Showing {filteredCompanies.length} of {companies.length} companies</div>
            </CardContent>
          </Card>
      </div>

      {/* Add / Edit Company Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-[560px] bg-white max-h-[90vh] overflow-y-auto" data-testid="company-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-blue-500">
              {editingCompany ? 'Edit Company' : 'Add New Company'}
            </DialogTitle>
          </DialogHeader>
          {editingCompany && companyForm._claim_count > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-700 flex items-center gap-2" data-testid="locked-fields-banner">
              <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m0 0a2 2 0 100-4 2 2 0 000 4zm6-6V7a6 6 0 10-12 0v4" /><rect x="4" y="13" width="16" height="8" rx="2" strokeWidth={2} /></svg>
              Some fields are locked because this company has {companyForm._claim_count} existing claim(s)
            </div>
          )}
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="c-dot" className="text-gray-700 mb-1 block">DOT#</Label>
              <div className="flex gap-2">
                <Input id="c-dot" placeholder="Enter DOT number" value={companyForm.dot_number} onChange={(e) => setCompanyForm({ ...companyForm, dot_number: e.target.value })} disabled={editingCompany && companyForm._claim_count > 0} data-testid="company-dot-input" />
                <Button
                  type="button"
                  variant="outline"
                  disabled={!companyForm.dot_number?.trim() || dotLooking || (editingCompany && companyForm._claim_count > 0)}
                  onClick={handleDotLookup}
                  className="shrink-0 border-blue-200 text-blue-600 hover:bg-blue-50"
                  data-testid="dot-lookup-btn"
                >
                  {dotLooking ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Lookup'}
                </Button>
              </div>
            </div>
            <div>
              <Label htmlFor="c-name" className="text-gray-700 mb-1 block">Company Name *</Label>
              <Input id="c-name" placeholder="Enter company name" value={companyForm.name} onChange={(e) => setCompanyForm({ ...companyForm, name: toProperCase(e.target.value) })} disabled={editingCompany && companyForm._claim_count > 0} data-testid="company-name-input" />
              {formErrors.name && <p className="text-red-500 text-xs mt-1">{formErrors.name}</p>}
            </div>
            {user?.account_type === 'provider' && userOrgs.length > 0 && (
              <div>
                <Label htmlFor="c-org" className="text-gray-700 mb-1 block">Organization</Label>
                <Select value={companyForm.org_id || '_none'} onValueChange={(v) => setCompanyForm({ ...companyForm, org_id: v === '_none' ? '' : v })}>
                  <SelectTrigger className="w-full" data-testid="company-org-select">
                    <SelectValue placeholder="Unassigned" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">Unassigned</SelectItem>
                    {userOrgs.map((o) => (
                      <SelectItem key={o.org_id} value={o.org_id}>{o.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label htmlFor="c-city" className="text-gray-700 mb-1 block">City</Label>
                <Input id="c-city" placeholder="City" value={companyForm.city} onChange={(e) => setCompanyForm({ ...companyForm, city: e.target.value })} disabled={editingCompany && companyForm._claim_count > 0} data-testid="company-city-input" />
              </div>
              <div>
                <Label htmlFor="c-state" className="text-gray-700 mb-1 block">State</Label>
                <Input id="c-state" placeholder="ST" value={companyForm.state} onChange={(e) => setCompanyForm({ ...companyForm, state: e.target.value })} disabled={editingCompany && companyForm._claim_count > 0} data-testid="company-state-input" />
              </div>
              <div>
                <Label htmlFor="c-zip" className="text-gray-700 mb-1 block">Zip</Label>
                <Input id="c-zip" placeholder="00000" value={companyForm.zip} onChange={(e) => setCompanyForm({ ...companyForm, zip: e.target.value })} disabled={editingCompany && companyForm._claim_count > 0} data-testid="company-zip-input" />
              </div>
            </div>
            <div>
              <Label htmlFor="c-contact" className="text-gray-700 mb-1 block">Contact Person</Label>
              <Input id="c-contact" placeholder="Enter contact person name" value={companyForm.contact} onChange={(e) => setCompanyForm({ ...companyForm, contact: toProperCase(e.target.value) })} data-testid="company-contact-input" />
            </div>
            <div>
              <Label htmlFor="c-email" className="text-gray-700 mb-1 block">Email</Label>
              <Input id="c-email" type="email" placeholder="email@example.com" value={companyForm.email} onChange={(e) => setCompanyForm({ ...companyForm, email: e.target.value })} data-testid="company-email-input" />
              {formErrors.email && <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>}
            </div>
            <div>
              <Label htmlFor="c-phone" className="text-gray-700 mb-1 block">Phone</Label>
              <Input
                id="c-phone"
                type="tel"
                placeholder="(555) 000-0000"
                value={companyForm.phone}
                onChange={(e) => setCompanyForm({ ...companyForm, phone: formatPhone(e.target.value) })}
                data-testid="company-phone-input"
              />
              {formErrors.phone && <p className="text-red-500 text-xs mt-1">{formErrors.phone}</p>}
            </div>
          </div>
          {editingCompany && <NotificationRecipientsCard companyName={editingCompany} />}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)} className="border-gray-200">Cancel</Button>
            <Button onClick={handleSaveCompany} className="bg-blue-500 hover:bg-blue-600 text-white" data-testid="company-save-btn">
              {editingCompany ? 'Save Changes' : 'Add Company'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={() => { setDeleteTarget(null); setBillingImpact(null); }}>
        <DialogContent className="sm:max-w-[400px] bg-white" data-testid="delete-confirm-dialog" hideClose>
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-red-600">Delete Company</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600 py-2">
            Are you sure you want to delete <span className="font-semibold">"{deleteTarget}"</span>? This action cannot be undone.
          </p>
          {billingImpact && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-xs text-blue-700 flex items-start gap-2" data-testid="delete-billing-impact">
              <CreditCard className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{billingImpact.message}</span>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeleteTarget(null); setBillingImpact(null); }} className="border-gray-200" data-testid="delete-cancel-btn">Cancel</Button>
            <Button onClick={() => handleDeleteCompany(deleteTarget)} className="bg-red-500 hover:bg-red-600 text-white" data-testid="delete-confirm-btn">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UpgradeLimitPrompt
        isOpen={limitPrompt.open}
        onClose={() => setLimitPrompt({ open: false, message: '' })}
        message={limitPrompt.message}
      />
    </div>
  );
};

export default Admin;
